function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
     document.body.classList.add(`menu-drawer-open`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.remove(`menu-drawer-close`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
      document.body.classList.add(`menu-drawer-open`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
      document.body.classList.remove(`menu-drawer-open`);
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Check if the content is already loaded
    if (this.innerHTML.trim() === '') {
      // Get the product URL
      const productUrl = this.dataset.url.split('?')[0];

      // Fetch and populate the content
      fetch(`${productUrl}?section_id=bulk-quick-order-list`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
         // console.error(e);
        });
    }
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);


class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    this.sliderControlLinksArray = Array.from(this.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderItemOffset * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}



// class SliderComponent extends HTMLElement {
//   constructor() {
//     super();
//     this.slider = this.querySelector('[id^="Slider-"]');
//     this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
//     this.enableSliderLooping = false;
//     this.currentPageElement = this.querySelector('.slider-counter--current');
//     this.pageTotalElement = this.querySelector('.slider-counter--total');
//     this.prevButton = this.querySelector('button[name="previous"]');
//     this.nextButton = this.querySelector('button[name="next"]');

//     if (!this.slider || !this.nextButton) return;

//     this.initPages();
//     const resizeObserver = new ResizeObserver((entries) => this.initPages());
//     resizeObserver.observe(this.slider);

//     this.slider.addEventListener('scroll', this.update.bind(this));
//     this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
//     this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
//   }

//   initPages() {
//     this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
//     if (this.sliderItemsToShow.length < 2) return;
//     this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
//     this.slidesPerPage = Math.floor(
//       (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
//     );
//     this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
//     this.update();
//   }

//   resetPages() {
//     this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
//     this.initPages();
//   }

//   update() {
//     // Temporarily prevents unneeded updates resulting from variant changes
//     // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
//     if (!this.slider || !this.nextButton) return;

//     const previousPage = this.currentPage;
//     this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

//     if (this.currentPageElement && this.pageTotalElement) {
//       this.currentPageElement.textContent = this.currentPage;
//       this.pageTotalElement.textContent = this.totalPages;
//     }

//     if (this.currentPage != previousPage) {
//       this.dispatchEvent(
//         new CustomEvent('slideChanged', {
//           detail: {
//             currentPage: this.currentPage,
//             currentElement: this.sliderItemsToShow[this.currentPage - 1],
//           },
//         })
//       );
//     }

//     if (this.enableSliderLooping) return;

//     if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
//       this.prevButton.setAttribute('disabled', 'disabled');
//     } else {
//       this.prevButton.removeAttribute('disabled');
//     }

//     if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
//       this.nextButton.setAttribute('disabled', 'disabled');
//     } else {
//       this.nextButton.removeAttribute('disabled');
//     }
//   }

//   isSlideVisible(element, offset = 0) {
//     const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
//     return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
//   }

//   onButtonClick(event) {
//     event.preventDefault();
//     const step = event.currentTarget.dataset.step || 1;
//     this.slideScrollPosition =
//       event.currentTarget.name === 'next'
//         ? this.slider.scrollLeft + step * this.sliderItemOffset
//         : this.slider.scrollLeft - step * this.sliderItemOffset;
//     this.setSlidePosition(this.slideScrollPosition);
//   }

//   setSlidePosition(position) {
//     this.slider.scrollTo({
//       left: position,
//     });
//   }
// }




customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
          initializeUpsellSwiper();
          setTimeout(() => {
    updateWishlist();
}, 1000);

        
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

document.addEventListener('DOMContentLoaded', () => {
  var vendorFilter = [];
  var minPrice = null;
  var maxPrice = null;
  var available = null;
  // Loop through all query parameters and extract the ones for filter.p.vendor
  var urlParams = new URLSearchParams(window.location.search);
  const metafieldFilters = [];
  var tagsFilter = [];
  urlParams.forEach(function(value, key) {
    if (key === 'filter.p.vendor') {
      vendorFilter.push(value); // Add the vendor value to the array
    } else if (key === 'filter.v.availability') {
      available = value; // Add the vendor value to the array
    } else if (key === 'filter.v.price.lte') {
      if (value != '') {
        maxPrice = value;
      }
    } else if (key === 'filter.v.price.gte') {
      if (value != '') {
        minPrice = value;
      }
    } else if (key.startsWith('filter.p.m.')) {
      const fullKey = key.split('filter.p.m.')[1];  // Remove the 'filter.p.m.' prefix
      const parts = fullKey.split('.');  // Split the remaining part by '.'
      
      if (parts.length >= 2) {
        const namespace = parts[0];  // The first part is the namespace
        const metafieldKey = parts.slice(1).join('.');  // The rest is the key

        //console.log(value+'&&&&&');
        
        // Decode the value (e.g., 'lm%2FW' becomes 'lm/W')
        //const decodedValue = decodeURIComponent(value.replace(/\+/g, ' '));
        //const cleanedValue = value.replace(/\+/g, ' ').replace(/\t/g, ' '); // Replace + and tab
        const cleanedValue = value.replace(/\t/g, ' '); // Replace tab
        const decodedValue = decodeURIComponent(cleanedValue); // Decode the value

        // Push the filter to the metafieldFilters array
        metafieldFilters.push({
          namespace: namespace,
          key: metafieldKey,
          value: decodedValue
        });
      }
    }
  });

  const priceRange = { min: minPrice, max: maxPrice };

  if (metafieldFilters.length === 0 && vendorFilter.length === 0 && !priceRange.min && !priceRange.max && available === null) {
    fetchAllProducts(); // Function to fetch all products
  } else {
    fetchFilteredProducts(metafieldFilters, vendorFilter, priceRange, available); // Function to fetch filtered products
  }
  window.filterProductsOfCollections = filterProductsOfCollections;
  
  function filterProductsOfCollections() {
    var checkedCheckboxesCount = $('.facets__item input[type="checkbox"]:checked').length;
    console.log(checkedCheckboxesCount+'NNNN')
    if (checkedCheckboxesCount > 0) {
      $('.facets__item input[type="checkbox"]:checked').each(function() {
          var checkboxValue = $(this).val();  // Get the value of the checked checkbox
          var checkboxName = $(this).attr('name');  // Get the ID of the checkbox
          console.log("Checked Checkbox Name: " + checkboxName + ", Value: " + checkboxValue);
      });
    }
    
    var vendorFilter = [];
    var minPrice = null;
    var maxPrice = null;
    var available = null;
    // Loop through all query parameters and extract the ones for filter.p.vendor
    var urlParams = new URLSearchParams(window.location.search);
    console.log(urlParams);
    const metafieldFilters = [];
    var tagsFilter = [];
    if (checkedCheckboxesCount > 0) {
      $('.facets__item input[type="checkbox"]:checked').each(function() {
        var value = $(this).val();  // Get the value of the checked checkbox
        var key = $(this).attr('name');  // Get the ID of the checkbox
        if (key === 'filter.p.vendor') {
          vendorFilter.push(value); // Add the vendor value to the array
        } else if (key === 'filter.v.availability') {
          available = value; // Add the vendor value to the array
        } else if (key === 'filter.v.price.lte') {
          if (value != '') {
            maxPrice = value;
          }
        } else if (key === 'filter.v.price.gte') {
          if (value != '') {
            minPrice = value;
          }
        } else if (key.startsWith('filter.p.m.')) {
          const fullKey = key.split('filter.p.m.')[1];  // Remove the 'filter.p.m.' prefix
          const parts = fullKey.split('.');  // Split the remaining part by '.'
          
          if (parts.length >= 2) {
            const namespace = parts[0];  // The first part is the namespace
            const metafieldKey = parts.slice(1).join('.');  // The rest is the key
            
            // Decode the value (e.g., 'lm%2FW' becomes 'lm/W')
            //const decodedValue = decodeURIComponent(value.replace(/\+/g, ' '));
            //const cleanedValue = value.replace(/\+/g, ' ').replace(/\t/g, ' '); // Replace + and tab
            const cleanedValue = value.replace(/\t/g, ' '); // Replace tab
            const decodedValue = decodeURIComponent(cleanedValue); // Decode the value
    
            // Push the filter to the metafieldFilters array
            metafieldFilters.push({
              namespace: namespace,
              key: metafieldKey,
              value: decodedValue
            });
          }
        }
      });
    }
  
    const priceRange = { min: minPrice, max: maxPrice };
  
    if (metafieldFilters.length === 0 && vendorFilter.length === 0 && !priceRange.min && !priceRange.max && available === null) {
      fetchAllProducts(); // Function to fetch all products
    } else {
      fetchFilteredProducts(metafieldFilters, vendorFilter, priceRange, available); // Function to fetch filtered products
    }

    setTimeout(() => {
        showBreadcrumbs();
    }, 1000);
  }

  function fetchAllProducts() {  
    $(".pre-rendered-products").each(function () {
      const collectionId = $(this).attr('data-colletcion-id');
      const products = document.querySelectorAll('.pre-rendered-products .collection-' + collectionId);
      const productList = document.getElementById('collection_products_' + collectionId);
      const paginationDiv = document.getElementById('pagination-collection-'+ $(this).attr('data-colletcion-id'));
      const sortingDiv = document.getElementById('sorting-collection-' + collectionId);

      let sortedProducts = []; // Declare sortedProducts globally
      // Pagination settings
      const productsPerPage = 12; // Number of products to display per page
      const totalPages = Math.ceil(products.length / productsPerPage);
      $(".products_count_"+$(this).attr('data-colletcion-id')).html('('+products.length+')')
      let currentPage = 1;
      let currentSortOrder = 'asc'; // Default sorting order

      const sortProducts = (order) => {
        //  console.log('Sorting products...');
          // Convert products to array and sort
sortedProducts = Array.from(products);
      
          // Log sorted products for debugging
       //   console.log(sortedProducts);
      
          // After sorting, render the current page again and update pagination
          renderPage(currentPage);
          renderPagination();
      };


      const updateProductCount = () => {
          const totalProducts = sortedProducts.length; // Total products available
          const startIndex = (currentPage - 1) * productsPerPage + 1; // First product on the current page
          const endIndex = Math.min(currentPage * productsPerPage, totalProducts); // Last product on the current page
          const currentPageCount = endIndex - startIndex + 1; // Total products displayed on this page
      
          // Update the text dynamically
          $(".products_count_" + collectionId).html(`(Showing ${currentPageCount} of ${totalProducts})`);
      };

  
      // Function to render products for the current page
      const renderPage = (page) => {
        productList.innerHTML = ''; // Clear current product list
        // Add table headings
        const table = document.createElement('table');
        table.classList.add('product-table');
        table.classList.add('table');
      
        // Create table header
        const thead = document.createElement('thead');
        thead.innerHTML = `
          <tr>
            <th>Name <div id="sorting-collection-${collectionId}"></div></th>
            <th>SKU</th>
            <th>Brand</th>
            <th>Voltage</th>
            <th>Certification</th>
            <th>Lumens per Watt</th>
            <th>Dimmable</th>
            <th>Price</th>
          </tr>
        `;
        table.appendChild(thead);
      
        // Create table body
        const tbody = document.createElement('tbody');
        // Determine start and end index for the current page
        const startIndex = (page - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
  
        // Append products for the current page
        sortedProducts.slice(startIndex, endIndex).forEach(product => {
          const product_id = product.getAttribute('data-id');
          const productName = product.getAttribute('data-title') || 'N/A';
          const vendor = product.getAttribute('data-vendor') || 'N/A';
          const sku = product.getAttribute('data-sku') || 'N/A';
           const voltage = product.getAttribute('data-product-metafields-ols-voltate') || 'N/A';
          const lumen_per_watt = product.getAttribute('data-product-metafields-ols-lpw') || 'N/A';
          const dimmable = product.getAttribute('data-product-metafields-ols-dimmable') || 'N/A';
          const certificates = product.getAttribute('data-certificates');
          const priceMin = product.getAttribute('data-display-min-price') || 'N/A';
          const priceMax = product.getAttribute('data-display-max-price') || 'N/A';
          const unitprice = product.getAttribute('data-unit-price');
          const priceDisplay = (priceMin === priceMax) ? priceMin : `${priceMin} - ${priceMax}`;
          const row = document.createElement('tr');
          row.classList.add('list-view-record');
          row.classList.add('row-product-'+product_id);
          row.setAttribute('data-product-id', product_id);
          row.setAttribute('onclick', `openProductDetail(${product_id})`);

          row.setAttribute('tabindex', '0');  // add_this
            row.addEventListener('keydown', function(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();  // Prevent default action
      openProductDetail(product_id);  // Trigger the click action manually
    }
  });
  
          row.innerHTML = `
            <td class="main-product-title-list" >${productName}</td>
            <td>${sku}</td>
            <td>${vendor}</td>
            <td>${voltage}</td>
            <td>${certificates}</td>
            <td>${lumen_per_watt}</td>
            <td>${dimmable}</td>
            <td><div class="prices"><span class="pack-price">${priceDisplay}<span class="unit-price">${unitprice}</span></div></td>
          `;
      
          tbody.appendChild(row);

          const secondRowHtml = $(".custom-product-display-" + product_id).find('tbody').html();
          const firstRow = tbody.querySelector('.row-product-' + product_id);
          if (firstRow) {
            firstRow.insertAdjacentHTML('afterend', secondRowHtml);  // Insert the second row after the first
          }
        });

        // Append the tbody to the table
        table.appendChild(tbody);

        
      
        // Append the table to the product list
        productList.appendChild(table);

        // Update product count display
        updateProductCount();
        
        addSortingControl(collectionId);
        renderShowProducts();
      };

   


const renderPagination = () => {
    const paginationDiv = document.getElementById('pagination-collection-' + $(this).attr('data-colletcion-id'));
    paginationDiv.innerHTML = ''; // Clear existing pagination

    const maxVisiblePages = 3; // Number of pages to show at once

    // Add "First" (<<) button
    const firstButton = document.createElement('button');
    firstButton.classList.add('pagination-btn');
    firstButton.innerText = '«';
    if (currentPage === 1) {
        firstButton.classList.add('disabled');
        firstButton.disabled = true;
    } else {
        firstButton.addEventListener('click', () => {
            currentPage = 1;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(firstButton);

    // Add "Previous" (<) button
    const prevButton = document.createElement('button');
    prevButton.classList.add('pagination-btn');
    prevButton.innerText = '‹';
    if (currentPage === 1) {
        prevButton.classList.add('disabled');
        prevButton.disabled = true;
    } else {
        prevButton.addEventListener('click', () => {
            currentPage--;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(prevButton);

    // Determine visible page range
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust range to include the last pages if necessary
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
        const firstPageButton = document.createElement('button');
        firstPageButton.classList.add('pagination-btn');
        firstPageButton.innerText = '1';
        firstPageButton.addEventListener('click', () => {
            currentPage = 1;
            renderPage(currentPage);
            renderPagination();
        });
        paginationDiv.appendChild(firstPageButton);

        if (startPage > 2) {
            const ellipsis = document.createElement('span');
            ellipsis.innerText = '...';
            ellipsis.classList.add('custom-dot-cp-ellipsis');
            paginationDiv.appendChild(ellipsis);
        }
    }

    // Create page buttons for the visible range
    for (let page = startPage; page <= endPage; page++) {
        const pageButton = document.createElement('button');
        pageButton.classList.add('pagination-btn');
        pageButton.innerText = page;

        if (page === currentPage) {
            pageButton.classList.add('active');
        }

        pageButton.addEventListener('click', () => {
            currentPage = page;
            renderPage(currentPage);
            renderPagination();
        });

        paginationDiv.appendChild(pageButton);
    }

    // Add ellipsis and last page if necessary
    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            const ellipsis = document.createElement('span');
            ellipsis.innerText = '...';
            ellipsis.classList.add('custom-dot-cp-ellipsis');
            paginationDiv.appendChild(ellipsis);
        }

        const lastPageButton = document.createElement('button');
        lastPageButton.classList.add('pagination-btn');
        lastPageButton.innerText = totalPages;
        lastPageButton.addEventListener('click', () => {
            currentPage = totalPages;
            renderPage(currentPage);
            renderPagination();
        });
        paginationDiv.appendChild(lastPageButton);
    }

    // Add "Next" (>) button
    const nextButton = document.createElement('button');
    nextButton.classList.add('pagination-btn');
    nextButton.innerText = '›';
    if (currentPage === totalPages) {
        nextButton.classList.add('disabled');
        nextButton.disabled = true;
    } else {
        nextButton.addEventListener('click', () => {
            currentPage++;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(nextButton);

    // Add "Last" (>>) button
    const lastButton = document.createElement('button');
    lastButton.classList.add('pagination-btn');
    lastButton.innerText = '»';
    if (currentPage === totalPages) {
        lastButton.classList.add('disabled');
        lastButton.disabled = true;
    } else {
        lastButton.addEventListener('click', () => {
            currentPage = totalPages;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(lastButton);
};





      // Function to add sorting controls
      /*const addSortingControl = () => {
          sortingDiv.innerHTML = ''; // Clear existing sorting controls

          const sortButton = document.createElement('button');
          sortButton.innerText = `Sort by Name (${currentSortOrder === 'asc' ? 'Descending' : 'Ascending'})`;
          sortButton.classList.add('sort-btn');
          sortButton.addEventListener('click', () => {
              currentSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
              sortProducts(currentSortOrder);
              currentPage = 1; // Reset to the first page
              renderPage(currentPage);
              renderPagination();
              addSortingControl(); // Update sorting button
          });

          sortingDiv.appendChild(sortButton);
      };*/

      const addSortingControl = (collectionId) => {
          const sortingDiv = document.getElementById(`sorting-collection-${collectionId}`);
          sortingDiv.innerHTML = ''; // Clear any existing sorting controls
      
          const sortButton = document.createElement('button');
          sortButton.innerText = `${currentSortOrder === 'asc' ? 'Des' : 'Asc'}`;
          sortButton.classList.add(currentSortOrder);
          sortButton.classList.add('sort-btn');
          sortButton.addEventListener('click', () => {
              currentSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
              sortProducts(currentSortOrder);
              currentPage = 1; // Reset to the first page
              renderPage(currentPage);
              renderPagination();
              addSortingControl(collectionId); // Update sorting button after click
          });
      
          sortingDiv.appendChild(sortButton); // Append the button inside the div
      };
  
      // Initialize the first page and render pagination controls
      if (products.length > 0) {
        sortProducts(currentSortOrder); // Initial sort
        renderPage(currentPage);
        renderPagination();
        //addSortingControl();
      } else {
        console.log('No products found in this collection.');
        const productList = document.getElementById('collection_products_' + $(this).attr('data-colletcion-id'));
        productList.innerHTML = ''; // Clear current list
  
        const table = document.createElement('table');
        table.classList.add('product-table');
        table.classList.add('table');
      
        // Create table header
        const thead = document.createElement('thead');
        thead.innerHTML = `
          <tr>
            <th>Name</th>
            <th>SKU</th>
            <th>Brand</th>
            <th>Voltage</th>
            <th>Certification</th>
            <th>Lumens per Watt</th>
            <th>Dimmable</th>
            <th>Price</th>
          </tr>
        `;
        table.appendChild(thead);
      
        // Create table body
        const tbody = document.createElement('tbody');
        const noProductsRow = document.createElement('tr');

        // Adjust the colspan to match the number of columns in the table
        const colspan = 8; // Change this value if the number of columns changes
        noProductsRow.innerHTML = `
          <td colspan="${colspan}" style="text-align: center; color: #888; font-size: 16px;">No products found</td>
        `;
  
        // Append the "No products found" row to the table body
        tbody.appendChild(noProductsRow);
        table.appendChild(tbody);
      
        // Append the table to the product list
        productList.appendChild(table);
      }
    });
 setTimeout(() => {
   updateWishlist();
}, 1000);

    
  }

  function fetchFilteredProducts(metafieldFilters, vendorFilter, priceRange, available) {
    console.log(metafieldFilters)
    $(".pre-rendered-products").each(function () {
      const collectionId = $(this).attr('data-colletcion-id');
      const products = document.querySelectorAll('.pre-rendered-products .collection-' + $(this).attr('data-colletcion-id'));
      const filteredProducts = Array.from(products).filter((product) => {
        // Extract attributes
        const vendor = product.getAttribute('data-vendor');
        const pricemin = parseFloat(parseInt(product.getAttribute('data-price-min')) / 100);
        const pricemax = parseFloat(parseInt(product.getAttribute('data-price-max')) / 100);
        const product_availability = product.getAttribute('data-availability');

        const groupedFilters = metafieldFilters.reduce((acc, filter) => {
          if (!acc[filter.namespace]) acc[filter.namespace] = {};
          if (!acc[filter.namespace][filter.key]) acc[filter.namespace][filter.key] = [];
          acc[filter.namespace][filter.key].push(filter.value);
          return acc;
        }, {});
        
        const matchesMetafields = Object.keys(groupedFilters).every((namespace) => {
          const namespaceFilters = groupedFilters[namespace];
        
          return Object.keys(namespaceFilters).every((key) => {
            const productMetafieldValue = product.getAttribute(`data-product-metafields-${namespace}-${key}`);
        
            if (!productMetafieldValue) return false; // If product doesn't have this metafield, it doesn't match

            if (key === 'wattagerange' || key === 'cct' || key === 'ratings' || key === 'ledfeatures' || key === 'lightfxtype') {
              const productMetafieldValues = productMetafieldValue.split(',').map(val => val.trim());
              return namespaceFilters[key].some(filterValue => productMetafieldValues.includes(filterValue));
            } else {
              const productMetafieldValues = productMetafieldValue ? [productMetafieldValue] : [];
              return namespaceFilters[key].some(filterValue => productMetafieldValues.includes(filterValue));
            }
          });
        });
        
        /*const matchesMetafields = metafieldFilters.length === 0 || metafieldFilters.some((filter) => {
          const metafieldValue = product.getAttribute(`data-product-metafields-${filter.namespace}-${filter.key}`);
          if (filter.key == 'wattagerange' || filter.key == 'cct' || filter.key == 'ratings' || filter.key == 'ledfeatures' || filter.key == 'lightfxtype') {
            // If the metafield value contains comma-separated values, split it into an array
            const metafieldValues = metafieldValue ? metafieldValue.split(',').map(val => val.trim()) : [];
            // Check if the filter value matches any of the comma-separated values
            return metafieldValues.includes(filter.value);  
          } else {
            const metafieldValues = metafieldValue ? [metafieldValue] : [];
            // Check if the filter value matches any of the comma-separated values
            return metafieldValues.includes(filter.value);
          } */
          /*
          // If the metafield value contains comma-separated values, split it into an array
          //const metafieldValues = metafieldValue ? metafieldValue.split(',').map(val => val.trim()) : [];
          const metafieldValues = metafieldValue ? [metafieldValue] : [];
          // Check if the filter value matches any of the comma-separated values
          return metafieldValues.includes(filter.value);*/
        //});
  
        // Vendor check
        const matchesVendor = vendorFilter.length === 0 || (vendor && vendorFilter.includes(vendor));
  
        // Availability check
        const matchesAvailability = available === null || (product_availability && product_availability == available);
  
        // Price range check
        const priceInRange = (!priceRange.min || pricemin >= priceRange.min) &&
          (!priceRange.max || pricemax <= priceRange.max);
  
        // Combine all conditions
        return matchesVendor && matchesAvailability && priceInRange && matchesMetafields;
      });

            const sortingDiv = document.getElementById('sorting-collection-' + collectionId);



      let sortedProducts = []; // Declare sortedProducts globally
      
      // Pagination settings
      const productsPerPage = 12; // Number of products per page
      const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
      $(".products_count_"+$(this).attr('data-colletcion-id')).html('('+filteredProducts.length+')')
      let currentPage = 1;
       let currentSortOrder = 'asc'; // Default sorting order



      const sortProducts = (order) => {

      //    console.log('Sorting products...');

          // Convert products to array and sort

       sortedProducts = Array.from(products);

      

          // Log sorted products for debugging

        //  console.log(sortedProducts);

      

          // After sorting, render the current page again and update pagination

          renderPage(currentPage);

          renderPagination();

      };


      const updateProductCount = () => {
          const totalProducts = sortedProducts.length; // Total products available
          const startIndex = (currentPage - 1) * productsPerPage + 1; // First product on the current page
          const endIndex = Math.min(currentPage * productsPerPage, totalProducts); // Last product on the current page
          const currentPageCount = endIndex - startIndex + 1; // Total products displayed on this page
      
          // Update the text dynamically
          $(".products_count_" + collectionId).html(`(Showing ${currentPageCount} of ${totalProducts})`);
      };

      
  
      // Function to render products for the current page
      const renderPage = (page) => {
        const productList = document.getElementById('collection_products_' + $(this).attr('data-colletcion-id'));
        productList.innerHTML = ''; // Clear current list

        const table = document.createElement('table');
        table.classList.add('product-table');
        table.classList.add('table');
      
        // Create table header
        const thead = document.createElement('thead');
        thead.innerHTML = `
          <tr>
             <th>Name <div id="sorting-collection-${collectionId}"></div></th>
            <th>SKU</th>
            <th>Brand</th>
            <th>Voltage</th>
            <th>Certification</th>
            <th>Lumens per Watt</th>
            <th>Dimmable</th>
            <th>Price</th>
          </tr>
        `;
        table.appendChild(thead);
      
        // Create table body
        const tbody = document.createElement('tbody');
  
        // Determine start and end index for the current page
        const startIndex = (page - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
  
        // Append products for the current page
      sortedProducts.slice(startIndex, endIndex).forEach(product => {
          const product_id = product.getAttribute('data-id');
          const productName = product.getAttribute('data-title') || 'N/A';
          const vendor = product.getAttribute('data-vendor') || 'N/A';
          const sku = product.getAttribute('data-sku') || 'N/A';
          const voltage = product.getAttribute('data-product-metafields-ols-voltate') || 'N/A';
          const lumen_per_watt = product.getAttribute('data-product-metafields-ols-lpw') || 'N/A';
          const dimmable = product.getAttribute('data-product-metafields-ols-dimmable') || 'N/A';
          const certificates = product.getAttribute('data-certificates');
          const priceMin = product.getAttribute('data-display-min-price') || 'N/A';
          const priceMax = product.getAttribute('data-display-max-price') || 'N/A';
          const priceDisplay = (priceMin === priceMax) ? priceMin : `${priceMin} - ${priceMax}`;
          const row = document.createElement('tr');
          row.classList.add('list-view-record');
          row.classList.add('row-product-'+product_id);
          row.setAttribute('data-product-id', product_id);
        row.setAttribute('onclick', `openProductDetail(${product_id})`);
          row.innerHTML = `
            <td class="main-product-title-list" >${productName}</td>
            <td>${sku}</td>
            <td>${vendor}</td>
            <td>${voltage}</td>
            <td>${certificates}</td>
            <td>${lumen_per_watt}</td>
            <td>${dimmable}</td>
            <td>${priceDisplay}</td>
          `;
      
          tbody.appendChild(row);

          const secondRowHtml = $(".custom-product-display-" + product_id).find('tbody').html();
          const firstRow = tbody.querySelector('.row-product-' + product_id);
          if (firstRow) {
            firstRow.insertAdjacentHTML('afterend', secondRowHtml);  // Insert the second row after the first
          }
        });

        // Append the tbody to the table
        table.appendChild(tbody);
      
        // Append the table to the product list
        productList.appendChild(table);

        updateProductCount();
        
         addSortingControl(collectionId);
        renderShowProducts();
      };

  const renderPagination = () => {
    const paginationDiv = document.getElementById('pagination-collection-' + $(this).attr('data-colletcion-id'));
    paginationDiv.innerHTML = ''; // Clear existing pagination

    const maxVisiblePages = 3; // Number of pages to show at once

    // Add "First" (<<) button
    const firstButton = document.createElement('button');
    firstButton.classList.add('pagination-btn');
    firstButton.innerText = '«';
    if (currentPage === 1) {
        firstButton.classList.add('disabled');
        firstButton.disabled = true;
    } else {
        firstButton.addEventListener('click', () => {
            currentPage = 1;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(firstButton);

    // Add "Previous" (<) button
    const prevButton = document.createElement('button');
    prevButton.classList.add('pagination-btn');
    prevButton.innerText = '‹';
    if (currentPage === 1) {
        prevButton.classList.add('disabled');
        prevButton.disabled = true;
    } else {
        prevButton.addEventListener('click', () => {
            currentPage--;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(prevButton);

    // Determine visible page range
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust range to include the last pages if necessary
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
        const firstPageButton = document.createElement('button');
        firstPageButton.classList.add('pagination-btn');
        firstPageButton.innerText = '1';
        firstPageButton.addEventListener('click', () => {
            currentPage = 1;
            renderPage(currentPage);
            renderPagination();
        });
        paginationDiv.appendChild(firstPageButton);

        if (startPage > 2) {
            const ellipsis = document.createElement('span');
            ellipsis.innerText = '...';
            ellipsis.classList.add('custom-dot-cp-ellipsis');
            paginationDiv.appendChild(ellipsis);
        }
    }

    // Create page buttons for the visible range
    for (let page = startPage; page <= endPage; page++) {
        const pageButton = document.createElement('button');
        pageButton.classList.add('pagination-btn');
        pageButton.innerText = page;

        if (page === currentPage) {
            pageButton.classList.add('active');
        }

        pageButton.addEventListener('click', () => {
            currentPage = page;
            renderPage(currentPage);
            renderPagination();
        });

        paginationDiv.appendChild(pageButton);
    }

    // Add ellipsis and last page if necessary
    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            const ellipsis = document.createElement('span');
            ellipsis.innerText = '...';
            ellipsis.classList.add('custom-dot-cp-ellipsis');
            paginationDiv.appendChild(ellipsis);
        }

        const lastPageButton = document.createElement('button');
        lastPageButton.classList.add('pagination-btn');
        lastPageButton.innerText = totalPages;
        lastPageButton.addEventListener('click', () => {
            currentPage = totalPages;
            renderPage(currentPage);
            renderPagination();
        });
        paginationDiv.appendChild(lastPageButton);
    }

    // Add "Next" (>) button
    const nextButton = document.createElement('button');
    nextButton.classList.add('pagination-btn');
    nextButton.innerText = '›';
    if (currentPage === totalPages) {
        nextButton.classList.add('disabled');
        nextButton.disabled = true;
    } else {
        nextButton.addEventListener('click', () => {
            currentPage++;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(nextButton);

    // Add "Last" (>>) button
    const lastButton = document.createElement('button');
    lastButton.classList.add('pagination-btn');
    lastButton.innerText = '»';
    if (currentPage === totalPages) {
        lastButton.classList.add('disabled');
        lastButton.disabled = true;
    } else {
        lastButton.addEventListener('click', () => {
            currentPage = totalPages;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(lastButton);
};

    // Function to add sorting controls

      /*const addSortingControl = () => {

          sortingDiv.innerHTML = ''; // Clear existing sorting controls



          const sortButton = document.createElement('button');

          sortButton.innerText = `Sort by Name (${currentSortOrder === 'asc' ? 'Descending' : 'Ascending'})`;

          sortButton.classList.add('sort-btn');

          sortButton.addEventListener('click', () => {

              currentSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';

              sortProducts(currentSortOrder);

              currentPage = 1; // Reset to the first page

              renderPage(currentPage);

              renderPagination();

              addSortingControl(); // Update sorting button

          });



          sortingDiv.appendChild(sortButton);

      };*/



      const addSortingControl = (collectionId) => {

          const sortingDiv = document.getElementById(`sorting-collection-${collectionId}`);

          sortingDiv.innerHTML = ''; // Clear any existing sorting controls

      

          const sortButton = document.createElement('button');

          sortButton.innerText = `${currentSortOrder === 'asc' ? 'Des' : 'Asc'}`;

          sortButton.classList.add(currentSortOrder);

          sortButton.classList.add('sort-btn');

          sortButton.addEventListener('click', () => {

              currentSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';

              sortProducts(currentSortOrder);

              currentPage = 1; // Reset to the first page

              renderPage(currentPage);

              renderPagination();

              addSortingControl(collectionId); // Update sorting button after click

          });

      

          sortingDiv.appendChild(sortButton); // Append the button inside the div

      };
      
      // Initialize the first page and render pagination controls
      if (filteredProducts.length > 0) {
         sortProducts(currentSortOrder); // Initial sort
        renderPage(currentPage);
        renderPagination();
      } else {
        console.log('No products found for the selected filters.');
        const productList = document.getElementById('collection_products_' + $(this).attr('data-colletcion-id'));
        productList.innerHTML = ''; // Clear current list
  
        const table = document.createElement('table');
        table.classList.add('product-table');
        table.classList.add('table');
      
        // Create table header
        const thead = document.createElement('thead');
        thead.innerHTML = `
          <tr>
            <th>Name</th>
            <th>SKU</th>
            <th>Brand</th>
            <th>Voltage</th>
            <th>Certification</th>
            <th>Lumens per Watt</th>
            <th>Dimmable</th>
            <th>Price</th>
          </tr>
        `;
        table.appendChild(thead);
      
        // Create table body
        const tbody = document.createElement('tbody');
        const noProductsRow = document.createElement('tr');

        // Adjust the colspan to match the number of columns in the table
        const colspan = 8; // Change this value if the number of columns changes
        noProductsRow.innerHTML = `
          <td colspan="${colspan}" style="text-align: center; color: #888; font-size: 16px;">No products found</td>
        `;
  
        // Append the "No products found" row to the table body
        tbody.appendChild(noProductsRow);
        table.appendChild(tbody);
      
        // Append the table to the product list
        productList.appendChild(table);
      }
    });

setTimeout(() => {
   updateWishlist();
}, 1000);

  }

  function renderShowProducts() {
    $(".dispplay-sub-collections").each(function(){
     
      if ($(this).hasClass('show-grid-view')) {
        
        $(this).find('tr').each(function(){
          if ($(this).hasClass('data-append')) {
            $(this).removeClass('append-display-none');
          }
        })
      }
    })
  }
});

function renderShowProductsRecentlyViewed() {
  $(".dispplay-sub-collections").each(function(){
    if ($(this).hasClass('show-grid-view')) {
      $(this).find('tr').each(function(){
        if ($(this).hasClass('data-append')) {
          $(this).removeClass('append-display-none');
        }
      })
    }
  })
}

function fetchRecentlyViewedProducts() {  
  //$(".pre-rendered-products").each(function () {
    const collectionId = $(this).attr('data-colletcion-id');
    //const products = document.querySelectorAll('.pre-rendered-products .collection-' + collectionId);
    const products = document.querySelectorAll('.collection-custom-recently-viewed');
    //const productList = document.getElementById('collection_products_' + collectionId);
    const productList = document.getElementById('collection_products_custom-recently-viewed');
    //const paginationDiv = document.getElementById('pagination-collection-'+ $(this).attr('data-colletcion-id'));
    const paginationDiv = document.getElementById('pagination-collection-custom-recently-viewed');
    // Pagination settings
    const productsPerPage = products.length; // Number of products to display per page
    const totalPages = Math.ceil(products.length / productsPerPage);
    //$(".products_count_"+$(this).attr('data-colletcion-id')).html('('+products.length+')')
    let currentPage = 1;

    // Function to render products for the current page
    const renderPage = (page) => {
      productList.innerHTML = ''; // Clear current product list
      // Add table headings
      const table = document.createElement('table');
      table.classList.add('product-table');
      table.classList.add('table');
    
      // Create table header
      const thead = document.createElement('thead');
      thead.innerHTML = `
        <tr>
          <th>Name</th>
          <th>SKU</th>
          <th>Brand</th>
          <th>Voltage</th>
          <th>Certification</th>
          <th>Lumens per Watt</th>
          <th>Dimmable</th>
          <th>Price</th>
        </tr>
      `;
      table.appendChild(thead);
    
      // Create table body
      const tbody = document.createElement('tbody');
      // Determine start and end index for the current page
      const startIndex = (page - 1) * productsPerPage;
      const endIndex = startIndex + productsPerPage;

      // Append products for the current page
      Array.from(products).slice(startIndex, endIndex).forEach((product) => {
        const product_id = product.getAttribute('data-id');
        const productName = product.getAttribute('data-title') || 'N/A';
        const vendor = product.getAttribute('data-vendor') || 'N/A';
        const sku = product.getAttribute('data-sku') || 'N/A';
      const voltage = product.getAttribute('data-product-metafields-ols-voltate') || 'N/A';
          const lumen_per_watt = product.getAttribute('data-product-metafields-ols-lpw') || 'N/A';
          const dimmable = product.getAttribute('data-product-metafields-ols-dimmable') || 'N/A';
        const certificates = product.getAttribute('data-certificates');
        const priceMin = product.getAttribute('data-display-min-price') || 'N/A';
        const priceMax = product.getAttribute('data-display-max-price') || 'N/A';
        const unitprice = product.getAttribute('data-unit-price');
        const priceDisplay = (priceMin === priceMax) ? priceMin : `${priceMin} - ${priceMax}`;
        const row = document.createElement('tr');
        row.classList.add('list-view-record');
        row.classList.add('row-product-'+product_id);
        row.setAttribute('data-product-id', product_id);
        row.setAttribute('onclick', `openProductDetail(${product_id})`);
        row.innerHTML = `
          <td class="main-product-title-list" >${productName}</td>
          <td>${sku}</td>
          <td>${vendor}</td>
          <td>${voltage}</td>
          <td>${certificates}</td>
          <td>${lumen_per_watt}</td>
          <td>${dimmable}</td>
          <td><div class="prices"><span class="pack-price">${priceDisplay}<span class="unit-price">${unitprice}</span></div></td>
        `;
    
        tbody.appendChild(row);

        const secondRowHtml = $(".custom-product-display-" + product_id).find('tbody').html();
        const firstRow = tbody.querySelector('.row-product-' + product_id);
        if (firstRow) {
          firstRow.insertAdjacentHTML('afterend', secondRowHtml);  // Insert the second row after the first
        }
      });

      // Append the tbody to the table
      table.appendChild(tbody);

      
    
      // Append the table to the product list
      productList.appendChild(table);

      renderShowProductsRecentlyViewed();
    };

const renderPagination = () => {
    const paginationDiv = document.getElementById('pagination-collection-' + $(this).attr('data-colletcion-id'));
    paginationDiv.innerHTML = ''; // Clear existing pagination

    const maxVisiblePages = 3; // Number of pages to show at once

    // Add "First" (<<) button
    const firstButton = document.createElement('button');
    firstButton.classList.add('pagination-btn');
    firstButton.innerText = '«';
    if (currentPage === 1) {
        firstButton.classList.add('disabled');
        firstButton.disabled = true;
    } else {
        firstButton.addEventListener('click', () => {
            currentPage = 1;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(firstButton);

    // Add "Previous" (<) button
    const prevButton = document.createElement('button');
    prevButton.classList.add('pagination-btn');
    prevButton.innerText = '‹';
    if (currentPage === 1) {
        prevButton.classList.add('disabled');
        prevButton.disabled = true;
    } else {
        prevButton.addEventListener('click', () => {
            currentPage--;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(prevButton);

    // Determine visible page range
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust range to include the last pages if necessary
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
        const firstPageButton = document.createElement('button');
        firstPageButton.classList.add('pagination-btn');
        firstPageButton.innerText = '1';
        firstPageButton.addEventListener('click', () => {
            currentPage = 1;
            renderPage(currentPage);
            renderPagination();
        });
        paginationDiv.appendChild(firstPageButton);

        if (startPage > 2) {
            const ellipsis = document.createElement('span');
            ellipsis.innerText = '...';
            ellipsis.classList.add('custom-dot-cp-ellipsis');
            paginationDiv.appendChild(ellipsis);
        }
    }

    // Create page buttons for the visible range
    for (let page = startPage; page <= endPage; page++) {
        const pageButton = document.createElement('button');
        pageButton.classList.add('pagination-btn');
        pageButton.innerText = page;

        if (page === currentPage) {
            pageButton.classList.add('active');
        }

        pageButton.addEventListener('click', () => {
            currentPage = page;
            renderPage(currentPage);
            renderPagination();
        });

        paginationDiv.appendChild(pageButton);
    }

    // Add ellipsis and last page if necessary
    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            const ellipsis = document.createElement('span');
            ellipsis.innerText = '...';
            ellipsis.classList.add('custom-dot-cp-ellipsis');
            paginationDiv.appendChild(ellipsis);
        }

        const lastPageButton = document.createElement('button');
        lastPageButton.classList.add('pagination-btn');
        lastPageButton.innerText = totalPages;
        lastPageButton.addEventListener('click', () => {
            currentPage = totalPages;
            renderPage(currentPage);
            renderPagination();
        });
        paginationDiv.appendChild(lastPageButton);
    }

    // Add "Next" (>) button
    const nextButton = document.createElement('button');
    nextButton.classList.add('pagination-btn');
    nextButton.innerText = '›';
    if (currentPage === totalPages) {
        nextButton.classList.add('disabled');
        nextButton.disabled = true;
    } else {
        nextButton.addEventListener('click', () => {
            currentPage++;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(nextButton);

    // Add "Last" (>>) button
    const lastButton = document.createElement('button');
    lastButton.classList.add('pagination-btn');
    lastButton.innerText = '»';
    if (currentPage === totalPages) {
        lastButton.classList.add('disabled');
        lastButton.disabled = true;
    } else {
        lastButton.addEventListener('click', () => {
            currentPage = totalPages;
            renderPage(currentPage);
            renderPagination();
        });
    }
    paginationDiv.appendChild(lastButton);
};



    // Initialize the first page and render pagination controls
    if (products.length > 0) {
      renderPage(currentPage);
      renderPagination();
    } else {
      console.log('No products found in this collection.');
      const productList = document.getElementById('collection_products_custom-recently-viewed');
      productList.innerHTML = ''; // Clear current list

      const table = document.createElement('table');
      table.classList.add('product-table');
      table.classList.add('table');
    
      // Create table header
      const thead = document.createElement('thead');
      thead.innerHTML = `
        <tr>
          <th>Name</th>
          <th>SKU</th>
          <th>Brand</th>
          <th>Voltage</th>
          <th>Certification</th>
          <th>Lumens per Watt</th>
          <th>Dimmable</th>
          <th>Price</th>
        </tr>
      `;
      table.appendChild(thead);
    
      // Create table body
      const tbody = document.createElement('tbody');
      const noProductsRow = document.createElement('tr');

      // Adjust the colspan to match the number of columns in the table
      const colspan = 8; // Change this value if the number of columns changes
      noProductsRow.innerHTML = `
        <td colspan="${colspan}" style="text-align: center; color: #888; font-size: 16px;">No products found</td>
      `;

      // Append the "No products found" row to the table body
      tbody.appendChild(noProductsRow);
      table.appendChild(tbody);
    
      // Append the table to the product list
      productList.appendChild(table);
    }
  //});
}

$(document).on('click', '.show_recent_viewed', function(){
 
})

function openProductDetail(id_product) {
  var $dataAppend = $(".row-product-"+id_product).next('.data-append');
  if ($dataAppend.hasClass('append-display-none')) {
    $(".row-product-"+id_product).parent().find('tr').removeClass('data-append-for');
    $(".row-product-"+id_product).parent().find('tr.data-append').addClass('append-display-none');
    $(".row-product-"+id_product).addClass('data-append-for')
    $dataAppend.removeClass('append-display-none')
  } else {
    $(".row-product-"+id_product).removeClass('data-append-for')
    $dataAppend.addClass('append-display-none')
  }
}


 function openProductOptions(button) {
    const modalId = $(button).attr("data-modal"); // Get the value of the data-modal attribute
    $("#" + modalId).attr("open", true); // Add the 'open' attribute to the modal with the matching ID
    
   
  }